/**
 * @file CommandPalette.js — Palette de commandes accessible via Ctrl+K / Cmd+K.
 * Permet la navigation rapide vers n'importe quelle page de l'application
 * en filtrant par nom. Utilise la bibliothèque cmdk (déjà installée) (R-0088).
 */
import { useEffect, useState, useCallback } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApp } from '../contexts/AppContext';
import { APP_ROUTES, getSectionViewForPath } from '../lib/routes';
import {
  LayoutDashboard, Users, UserCircle, Shield, Settings,
  Calendar, Link2, Search,
} from 'lucide-react';

/**
 * Éléments de navigation disponibles dans la palette, organisés par groupe.
 * Chaque élément contient le chemin, le libellé, l'icône et le rôle minimum requis.
 */
const NAV_ITEMS = [
  { groupKey: 'command_palette.group_user', items: [
    { to: APP_ROUTES.dashboard, labelKey: 'nav.dashboard', icon: LayoutDashboard },
    { to: APP_ROUTES.circles, labelKey: 'nav.my_circles', icon: Users },
    { to: APP_ROUTES.events, labelKey: 'nav.events', icon: Calendar },
    { to: APP_ROUTES.profile, labelKey: 'nav.profile', icon: UserCircle },
  ]},
  { groupKey: 'command_palette.group_admin', items: [
    { to: APP_ROUTES.admin, labelKey: 'command_palette.admin_overview', icon: Shield, requireAdmin: true },
    { to: APP_ROUTES.adminMembers, labelKey: 'nav.members', icon: Users, requireAdmin: true },
    { to: APP_ROUTES.adminInvites, labelKey: 'nav.invites', icon: Link2, requireAdmin: true },
    { to: APP_ROUTES.adminEvents, labelKey: 'nav.events', icon: Calendar, requireAdmin: true },
  ]},
  { groupKey: 'command_palette.group_site', items: [
    { to: APP_ROUTES.site, labelKey: 'command_palette.site_overview', icon: Settings, requireSite: true },
    { to: APP_ROUTES.siteCircles, labelKey: 'command_palette.site_circles', icon: Users, requireSite: true },
  ]},
];

/**
 * Composant de la palette de commandes.
 * S'ouvre avec Ctrl+K (Windows/Linux) ou Cmd+K (Mac).
 * Se ferme en appuyant sur Escape ou en sélectionnant un élément.
 * @returns {React.ReactElement|null}
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { availableViews, setCurrentView } = useApp();

  /* Écouter le raccourci clavier global Ctrl+K / Cmd+K */
  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setOpen(prev => !prev);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /** Naviguer vers la destination et fermer la palette. */
  const handleSelect = (to) => {
    const targetView = getSectionViewForPath(to);
    if (targetView && availableViews.includes(targetView)) {
      setCurrentView(targetView);
    }
    navigate(to);
    setOpen(false);
  };

  if (!open) return null;

  const isAdmin = availableViews.includes('admin');
  const isSite = availableViews.includes('site');

  return (
    <div className="fixed inset-0 z-[100]" data-testid="command-palette">
      {/* Fond semi-transparent qui ferme la palette au clic */}
      <div
        className="fixed inset-0 bg-black/50"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(false); }}
      />

      {/* Conteneur centré de la palette */}
      <div className="fixed left-1/2 top-1/4 -translate-x-1/2 w-full max-w-lg">
        <Command
          className="bg-background border border-border rounded-md shadow-2xl overflow-hidden"
          label={t('command_palette.label')}
        >
          <div className="flex items-center gap-2 px-3 border-b border-border">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Command.Input
              placeholder={t('command_palette.search_placeholder')}
              className="w-full py-3 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
            />
          </div>

          <Command.List className="max-h-[300px] overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              {t('command_palette.no_results')}
            </Command.Empty>

            {NAV_ITEMS.map(group => {
              /* Filtrer les groupes selon les permissions */
              const filteredItems = group.items.filter(item => {
                if (item.requireAdmin && !isAdmin) return false;
                if (item.requireSite && !isSite) return false;
                return true;
              });
              if (filteredItems.length === 0) return null;

              return (
                <Command.Group key={group.groupKey} heading={t(group.groupKey)} className="text-xs text-muted-foreground font-medium px-2 py-1.5">
                  {filteredItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <Command.Item
                        key={item.to}
                        value={t(item.labelKey)}
                        onSelect={() => handleSelect(item.to)}
                        className="flex items-center gap-2 px-2 py-2 text-sm rounded-sm cursor-pointer aria-selected:bg-accent"
                      >
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {t(item.labelKey)}
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              );
            })}
          </Command.List>

          <div className="border-t border-border px-3 py-2 text-[10px] text-muted-foreground">
            <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">↑↓</kbd> {t('command_palette.navigate')} · <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">Enter</kbd> {t('command_palette.select')} · <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">Esc</kbd> {t('command_palette.close')}
          </div>
        </Command>
      </div>
    </div>
  );
}
